import React, { FC, useCallback, useEffect } from 'react';
import { SortDirection } from '@tanstack/react-table';
import { SystemIconButton } from '../../Icon/SystemIconButton';
import { useCompare } from '../../../hooks/useCompare';
import { useDataTableContext } from '../utils/DataTableContext';
import { useIntlUds } from '../../../translations/UdsIntlProvider';

interface Props {
  sorted: false | SortDirection;
  id: string;
  onToggleSorting: (desc?: boolean | undefined, isMulti?: boolean | undefined) => void;
}

export const DataTableSortsElements: FC<React.PropsWithChildren<Props>> = ({ onToggleSorting, sorted, id }) => {
  const { intl } = useIntlUds();
  const { setPageIndex, selectedRows, manualSortBy, getState, resetSorting } = useDataTableContext();
  const hasSortChanged = useCompare(sorted);

  const sortHandler = useCallback(() => {
    if (manualSortBy) {
      setPageIndex?.(0);
    }
    // keep previous react-table v7 sorting order
    const state = getState?.();
    const isSorted = state?.sorting && state?.sorting.length > 0;
    const sortDesc = state?.sorting[0]?.desc;
    const currentId = state?.sorting[0]?.id;
    if (!isSorted || currentId !== id) {
      onToggleSorting(false);
    } else if (!sortDesc) {
      onToggleSorting(true);
    } else {
      resetSorting?.();
    }
  }, [manualSortBy, getState, id, setPageIndex, onToggleSorting, resetSorting]);

  const blockSorting = selectedRows && selectedRows.length > 0;

  useEffect(() => {
    if (hasSortChanged && manualSortBy) {
      setPageIndex?.(0);
    }
  }, [setPageIndex, hasSortChanged, manualSortBy]);

  return sorted ? (
    sorted === 'desc' ? (
      <SystemIconButton
        tooltipText={intl.DataTable.Sort.tooltipText}
        aria-label="SortDown"
        className="sort__button"
        iconName="arrow_downward"
        dataTest={`${id}-sort-down`}
        onClick={sortHandler}
        disabled={blockSorting}
      />
    ) : (
      <SystemIconButton
        tooltipText={intl.DataTable.Sort.tooltipText}
        aria-label="SortUp"
        className="sort__button"
        iconName="arrow_upward"
        dataTest={`${id}-sort-up`}
        onClick={sortHandler}
        disabled={blockSorting}
      />
    )
  ) : (
    <SystemIconButton
      tooltipText={intl.DataTable.Sort.tooltipText}
      aria-label="Sort"
      className="sort__button"
      iconName="swap_vert"
      dataTest={`${id}-sort`}
      onClick={sortHandler}
      disabled={blockSorting}
    />
  );
};
